import React from "react";
import { ExternalLink } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { formatDateTime } from "../../shared/utils/format";
import { useSearchParamUpdater } from "../../shared/hooks/useSearchParamUpdater";
import { useUserSection } from "./useUserSection";
import { Column, DataTable, LoadState, Muted, Pagination, SectionCard, StatusBadge } from "./ui";
import type { ErrorRow, ErrorsExtra, Paged, PublishAttempt } from "./types";

const PAGE_SIZE = 20;
const PLATFORMS = ["instagram", "facebook", "twitter", "linkedin", "youtube", "google_business"];
const KIND_LABEL: Record<ErrorRow["kind"], string> = { publish: "Publish", generation: "Generation", week: "Week" };
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const SentryLink: React.FC<{ href: string; label?: string }> = ({ href, label = "Sentry" }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1 whitespace-nowrap text-violet-600 hover:underline"
  >
    {label}
    <ExternalLink className="h-3 w-3" />
  </a>
);

function providerText(a: PublishAttempt): string {
  const parts = [
    a.providerStatus ? `HTTP ${a.providerStatus}` : null,
    a.providerCode ? `code ${a.providerCode}` : null,
    a.providerSubcode ? `sub ${a.providerSubcode}` : null,
  ].filter(Boolean);
  return parts.length ? ` (${parts.join(", ")})` : "";
}

function AttemptHistory({ attempts }: Readonly<{ attempts: PublishAttempt[] }>) {
  if (!attempts.length) return null;
  return (
    <details className="mt-1 text-xs">
      <summary className="cursor-pointer text-gray-500">{attempts.length} attempt(s)</summary>
      <ol className="mt-1 space-y-1">
        {attempts.map((a) => (
          <li key={`${a.at}-${a.outcome}`} className="text-gray-600">
            <span className="font-medium">{formatDateTime(a.at)}</span> · {a.source.replaceAll("_", " ")} ·{" "}
            <span className={a.outcome === "success" ? "text-green-700" : "text-red-600"}>{a.outcome}</span>
            {a.final ? " · final" : ""}
            {providerText(a)}
            {a.message ? ` — ${a.message}` : ""}
          </li>
        ))}
      </ol>
    </details>
  );
}

const columns: Column<ErrorRow>[] = [
  { header: "When", render: (r) => <span className="whitespace-nowrap">{formatDateTime(r.occurredAt)}</span> },
  { header: "Brand", render: (r) => <span className="whitespace-nowrap">{r.businessName || `#${r.clientId}`}</span> },
  {
    header: "What failed",
    render: (r) => (
      <span className="flex flex-col gap-1">
        <StatusBadge status={KIND_LABEL[r.kind]} />
        {r.platform && <span className="capitalize text-gray-600">{r.platform.replaceAll("_", " ")}</span>}
        {r.weekStartDate && <Muted>Week of {r.weekStartDate}</Muted>}
      </span>
    ),
  },
  {
    header: "Topic / post",
    render: (r) => (
      <span className="block max-w-xs">
        <span className="line-clamp-2" title={r.topic ?? undefined}>{r.topic || "—"}</span>
        <Muted>
          {r.postId ? `Post #${r.postId}` : ""}
          {r.planEntryId ? ` · Topic #${r.planEntryId}` : ""}
        </Muted>
      </span>
    ),
  },
  {
    header: "Error",
    render: (r) => (
      <span className="block max-w-md">
        <span className="text-xs text-red-600 line-clamp-3" title={r.message ?? undefined}>{r.message || "—"}</span>
        <AttemptHistory attempts={r.attemptLog} />
      </span>
    ),
  },
  { header: "", render: (r) => <SentryLink href={r.sentryUrl} /> },
];

export const ErrorsTab: React.FC<{ userId: number; clientId?: number }> = ({ userId, clientId }) => {
  const [searchParams] = useSearchParams();
  const updateSearchParams = useSearchParamUpdater();
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "", 10) || 1);
  const platformParam = searchParams.get("platform") ?? "";
  const platform = PLATFORMS.includes(platformParam) ? platformParam : "";
  const from = DAY.test(searchParams.get("from") ?? "") ? searchParams.get("from") ?? "" : "";
  const to = DAY.test(searchParams.get("to") ?? "") ? searchParams.get("to") ?? "" : "";

  const { data, loading, error, reload } = useUserSection<Paged<ErrorRow> & ErrorsExtra>(userId, "errors", {
    clientId,
    platform: platform || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const filters = (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <select
        value={platform}
        onChange={(e) => updateSearchParams({ platform: e.target.value, page: null })}
        className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-gray-700"
      >
        <option value="">All failures</option>
        {PLATFORMS.map((p) => (
          <option key={p} value={p}>
            {p.replaceAll("_", " ")} publishes
          </option>
        ))}
      </select>
      <input
        type="date"
        aria-label="From date"
        value={from}
        onChange={(e) => updateSearchParams({ from: e.target.value, page: null })}
        className="rounded-lg border border-gray-200 px-2 py-1"
      />
      <input
        type="date"
        aria-label="To date"
        value={to}
        onChange={(e) => updateSearchParams({ to: e.target.value, page: null })}
        className="rounded-lg border border-gray-200 px-2 py-1"
      />
    </div>
  );

  return (
    <SectionCard title="Errors" action={filters}>
      <LoadState loading={loading} error={error} onRetry={reload} />
      {!loading && !error && data && (
        <>
          <p className="mb-3 text-sm text-gray-600">
            All of this user’s errors in Sentry: <SentryLink href={data.sentry.userUrl} label="Open" />
            {data.sentry.clients.length > 1 && " · per brand: "}
            {data.sentry.clients.length > 1 &&
              data.sentry.clients.map((c) => (
                <span key={c.clientId} className="mr-2">
                  <SentryLink href={c.url} label={`#${c.clientId}`} />
                </span>
              ))}
          </p>
          <DataTable columns={columns} rows={data.items} emptyText="No failures recorded." />
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            totalCount={data.totalCount}
            onChange={(next) => updateSearchParams({ page: next > 1 ? next : null })}
          />
        </>
      )}
    </SectionCard>
  );
};
